import axios from "axios";
import React, { useEffect, useState } from "react";
import { GET_LEAVE_REPORT_BY_BDM } from "../../constants";
import {
  Card,
  CardContent,
  Collapse,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  Table,
  Grid,
  Box,
  Fab,
} from "@mui/material";
import { AlertCircle, Calendar, Clock, FileText } from "lucide-react";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import LeaveComponent from "../ToDoList/LeaveComponent";

const LeaveReport = () => {
  const employeeId = localStorage.getItem("username");

  const [reportData, setReportData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReportData = async () => {
    try {
      const response = await axios.get(
        `${GET_LEAVE_REPORT_BY_BDM}?employeeId=${employeeId}`
      );
      setReportData(response.data.leaves);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  // Function to format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Function to calculate duration in days
  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Including both start and end days
  };

  // Function to get leave type badge color
  const getLeaveTypeColor = (leaveType) => {
    switch (leaveType) {
      case "SICK_LEAVE":
        return "bg-red-100 text-red-800";
      case "UNPAID_LEAVE":
        return "bg-blue-100 text-blue-800";
      case "REGIONAL_HOLIDAY":
        return "bg-green-100 text-green-800";
      case "PAID_LEAVE":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const theme = useTheme();
  const isMobileOrPhablet = useMediaQuery("(max-width: 1080px)");

  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const [openLeaveModal, setOpenLeaveModal] = useState(false);

  const handleCloseLeaveModal = () => {
    setOpenLeaveModal(false);
  };
  const handleAddClick = () => {
    setOpenLeaveModal(true);
  };

  return (
    <>
      {isMobileOrPhablet ? (
        <>
          <div className="p-1">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Employee Leaves
              </h2>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                Total: {totalCount} Request
              </div>
            </div>

            {/* Check if there's no data */}
            {reportData.length === 0 ? (
              <div className="text-center text-gray-500 py-6">
                <p className="text-xl font-medium">No data available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {reportData.map((leave) => (
                  <Card
                    key={leave.id}
                    className="overflow-hidden border-t-4 border-indigo-500 hover:shadow-lg transition-shadow"
                  >
                    <CardContent className="p-0">
                      <div className="bg-gray-50 p-3 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${getLeaveTypeColor(
                              leave.LeaveType
                            )}`}
                          >
                            {leave.LeaveType.replace("_", " ")}
                          </span>
                        </div>
                        <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm font-medium flex items-center gap-1">
                          <Clock size={16} />
                          {calculateDuration(
                            leave.StartDate,
                            leave.EndDate
                          )}{" "}
                          days
                        </div>
                      </div>

                      <div className="p-2">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-start gap-3">
                            <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-gray-700">
                                Date
                              </p>
                              <p className="text-gray-600">
                                {formatDate(leave.StartDate)} -{" "}
                                {formatDate(leave.EndDate)}
                              </p>
                            </div>
                          </div>

                          {/* <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">Employee ID</p>
                            <p className="text-gray-600">{leave.EmployeeId}</p>
                          </div>
                        </div> */}

                          {leave.Remarks && (
                            <div className="flex items-start gap-3">
                              <FileText className="h-5 w-5 text-gray-500 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  Remarks
                                </p>
                                <p className="text-gray-600">{leave.Remarks}</p>
                              </div>
                            </div>
                          )}

                          <div className="text-xs text-gray-400 mt-2">
                            Created on{" "}
                            {new Date(leave.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <Box
            sx={{
              position: "fixed",
              bottom: `calc(16px + ${isMobileOrPhablet ? "56px" : "0px"})`,
              right: 16,
              zIndex: 10,
            }}
          >
            <Fab
              color="primary"
              aria-label="add lead"
              onClick={handleAddClick}
              sx={{
                backgroundColor: "primary", // Customize FAB color
                "&:hover": {
                  backgroundColor: "#115293",
                },
              }}
            >
              <AddIcon />
            </Fab>
          </Box>

          <LeaveComponent
            open={openLeaveModal}
            onClose={handleCloseLeaveModal}
            pageType={'Leave report'}
            refresh ={fetchReportData}
          ></LeaveComponent>
        </>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Employee Leaves
            </h2>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
              Total: {totalCount} leaves
            </div>
          </div>

          <Paper>
            <TableContainer
              sx={{
                boxShadow: 2,
                borderRadius: "8px",
                margin: "16px 0",
              }}
            >
              <Table
                sx={{
                  "& .MuiTableCell-root": {
                    border: "1px solid rgba(224, 224, 224, 1)",
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#EEF0F4" }}>
                    <TableCell align="center">SNo</TableCell>
                    <TableCell align="center">
                      <strong>Leave Type</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Start Date</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>End Date</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Remarks</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Created Date</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No Data Available Yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {reportData.map((item, index) => {
                        const {
                          StartDate,
                          EndDate,
                          LeaveType,
                          Remarks,
                          createdAt,
                        } = item;

                        const formattedStartDate = new Date(
                          StartDate
                        ).toLocaleDateString();
                        const formattedEndDate = new Date(
                          EndDate
                        ).toLocaleDateString();

                        return (
                          <React.Fragment key={item.id}>
                            {/* Main Row */}
                            <TableRow
                              sx={{
                                backgroundColor:
                                  index % 2 === 0 ? "#FFFFFF" : "#F9FAFB",
                                "&:hover": { backgroundColor: "#F1F5F9" },
                              }}
                            >
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  color: getLeaveTypeColor(LeaveType),
                                  fontWeight: "600",
                                }}
                              >
                                {LeaveType}
                              </TableCell>
                              <TableCell align="center">
                                {formattedStartDate}
                              </TableCell>
                              <TableCell align="center">
                                {formattedEndDate}
                              </TableCell>
                              <TableCell align="center">{Remarks}</TableCell>
                              <TableCell align="center">
                                {formatDate(createdAt)}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </>
  );
};

export default LeaveReport;
